import React, { useContext, useEffect, useMemo } from 'react';
import { ProductTransaction, ProductTransactionType } from '../../../../../../../../types/productTransaction';
import { t } from '../../../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../../../types/translation/TranslationKey';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';
import { TextInput } from '../../../../../../../components/TextInput';
import { Checkbox } from '../../../../../../../components/Checkbox';
import { KitTemplateContext } from '../../../../../../../../context/KitTemplateContext';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import { KitContext } from '../../../../../../../../context/KitContext';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { UnitOfMeasure } from '../../../../../../../../types/uom';

interface ProductTransactionDestinationKitProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;
  productMasterData?: ProductMasterData;
  disabled?: boolean;
}

export default function ProductTransactionDestinationKit({
  transaction,
  setTransaction,
  productMasterData: pmd,
  disabled = false,
}: ProductTransactionDestinationKitProps) {
  if (transaction.type === ProductTransactionType.outbound) return null;
  if (!pmd) return null;

  const { productMasterData } = useContext(ProductMasterDataContext);
  const { kitTemplates } = useContext(KitTemplateContext);
  const { kits } = useContext(KitContext);
  const { products } = useContext(ProductContext);

  const kitManaged = pmd.kitManaged;

  const items = useMemo(() => {
    return [...kitTemplates.values()].filter(
      kt => kt.companyId === transaction.companyId && kt.productMasterDataId === pmd.id,
    );
  }, [pmd, kitTemplates]);

  const potentialKits = useMemo(() => {
    const allProducts = [...products.values()].flatMap(p => [...p.values()]);

    return [...kits.values()].filter(kit => {
      if (kit.companyId !== transaction.companyId) return false;

      const template = kitTemplates.get(kit.templateId);
      if (!template) return false;

      const templateProduct = template.products.find(product => product.productMasterDataId === pmd.id);
      if (!templateProduct) return false;

      const hasMainKitProductInDestination = allProducts.some(
        product =>
          product.productMasterDataId === template.productMasterDataId &&
          product.kitId === kit.id &&
          (!transaction.product.toStockLocationId ||
            product.stockLocationId === transaction.product.toStockLocationId) &&
          (!transaction.product.toBinId || product.binId === transaction.product.toBinId),
      );
      if (!hasMainKitProductInDestination) return false;

      const totalUnitQuantity = allProducts
        .filter(product => product.kitId === kit.id && product.productMasterDataId === pmd.id)
        .reduce((sum, product) => sum + BigInt(product.unitQuantity), BigInt(0));

      const quantity = parseFloat(UnitOfMeasure.toQuantity(pmd.unitOfMeasure, totalUnitQuantity.toString()));
      if (Number.isNaN(quantity)) return false;

      return quantity < templateProduct.quantity;
    });
  }, [kits, kitTemplates, pmd, products, transaction.product.toStockLocationId, transaction.product.toBinId]);

  useEffect(() => {
    if (items.length === 1 && !transaction.product.kit?.templateId) {
      setTransaction(transaction => transaction.withKitTemplateId(items[0].id));
    }
  }, [items]);

  const pmdPartOfKitTemplate = [...kitTemplates.values()].some(kt =>
    kt.products.some(p => p.productMasterDataId === pmd.id),
  );

  if (!kitManaged && !pmdPartOfKitTemplate) return null;

  const isDisabled = disabled || (kitManaged && transaction.type === ProductTransactionType.move);
  const showTemplate = pmd.kitManaged && transaction.type === ProductTransactionType.inbound;

  return (
    <div className='w-full p-2'>
      <div className='grid grid-cols-1 gap-2'>
        <div className='grid grid-cols-2 gap-2'>
          {showTemplate ? (
            <DropdownSearchSelect
              mandatory={true}
              disabled={disabled || isDisabled}
              label={t(TranslationKey.kitTemplate).singular.label}
              placeholder={t(TranslationKey.kitTemplate).singular.label}
              items={items}
              textBuilder={item => item.name}
              value={kitTemplates.get(transaction.product.kit?.templateId || '') || transaction.metadata?.kitTemplate}
              onSelect={item => {
                setTransaction(transaction => transaction.withKitTemplateId(item?.id));
              }}
            />
          ) : null}
          {pmd.kitManaged ? (
            <TextInput
              disabled={disabled || isDisabled}
              value={
                kits.get(transaction.product.kit?.toKitId || '')?.identifier ||
                transaction.metadata?.toKit?.identifier ||
                transaction.product.kit?.toKitId
              }
              label={t(TranslationKey.kit).singular.label}
              onChange={item => setTransaction(transaction => transaction.withToKitId(item))}
            />
          ) : (
            <DropdownSearchSelect
              disabled={disabled || !transaction.product.toBinId}
              value={kits.get(transaction.product.kit?.toKitId || '') || transaction.metadata?.toKit}
              onSelect={item =>
                setTransaction(transaction => transaction.withToKitId(item?.id).withKitTemplateId(item?.templateId))
              }
              label={t(TranslationKey.kit).singular.label}
              placeholder={t(TranslationKey.kit).singular.label}
              items={potentialKits}
              textBuilder={item => item.identifier}
            />
          )}
        </div>
        {pmd.kitManaged ? (
          <div>
            <Checkbox
              disabled={disabled}
              value={transaction.product.kit?.complete || false}
              onChange={value => setTransaction(transaction => transaction.withKitComplete(value))}
              label={t(TranslationKey.kitComplete).singular.label}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
